import { wrapAngle } from '../core/math';

// How a ship's officer would put numbers into words: compass points,
// bearings in three figures, distances in sea miles, speeds in knots.

export const METRES_PER_NM = 1852;
export const KNOTS_PER_MS = 1.943844;

const POINTS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];

/** Radians clockwise from north -> whole degrees 0..359. */
export function degrees(rad: number): number {
  return Math.round(((((rad * 180) / Math.PI) % 360) + 360) % 360) % 360;
}

export function three(deg: number): string {
  return `${String(deg).padStart(3, '0')}°`;
}

/** "NE 042°" */
export function bearingText(rad: number): string {
  const d = degrees(rad);
  return `${POINTS[Math.round(d / 22.5) % 16]} ${three(d)}`;
}

/** Distance in sea miles: "2.4 nm", "0.35 nm". */
export function nmText(m: number): string {
  const nm = m / METRES_PER_NM;
  return `${nm < 1 ? nm.toFixed(2) : nm.toFixed(1)} nm`;
}

export function knotsText(ms: number): string {
  return `${(ms * KNOTS_PER_MS).toFixed(1)} knots`;
}

/** True bearing (radians, clockwise from north) from one world point to another. */
export function bearingTo(fromX: number, fromZ: number, toX: number, toZ: number): number {
  return Math.atan2(toX - fromX, -(toZ - fromZ));
}

/** Where she lies from our bow: "on our starboard bow", "right astern"... */
export function relBearing(rel: number): string {
  const r = wrapAngle(rel);
  const a = Math.abs(r);
  const side = r >= 0 ? 'starboard' : 'port';
  if (a < 0.1) return 'dead ahead';
  if (a > Math.PI - 0.12) return 'dead astern';
  if (a < 0.45) return `fine on our ${side} bow`;
  if (a < 1.2) return `on our ${side} bow`;
  if (a < 1.95) return `abeam to ${side}`;
  if (a < 2.7) return `on our ${side} quarter`;
  return `fine on our ${side} quarter`;
}

/** Which way she shows herself to us: bows-on, broadside or stern. */
export function aspect(herHeading: number, bearingFromUs: number): string {
  // Angle between her course and the line from her back to us.
  const toUs = bearingFromUs + Math.PI;
  const a = Math.abs(wrapAngle(toUs - herHeading));
  if (a < 0.5) return 'bows-on to us';
  if (a > Math.PI - 0.5) return 'showing us her stern';
  return 'showing us her broadside';
}

export function agoText(seconds: number): string {
  if (seconds < 60) return 'moments ago';
  const m = Math.round(seconds / 60);
  return `${m} min ago`;
}
